import { type Report, ReportSchema } from '@zevik/schemas';
import { detailOf, identityHeaders, ReadFailedError, type Source } from './client';

/**
 * Reading a report: the list, and one report's bytes
 * (docs/DECISIONS.md ZD-125 §5).
 *
 * ## The bytes are the report
 *
 * `GET /v1/reports/:reportId/content` answers the stored markdown, digest-
 * checked by the store before it is served. What this returns is that text
 * and nothing derived from it: the page renders it (`report/markdown.ts`)
 * and the download hands over the same string, so a person reading in the
 * page and a person opening the file read one artefact. There is no second
 * copy of the report's content anywhere in this app — whatever is shown was
 * assembled by `assembleReport` and stored.
 *
 * ## The filename is the route's
 *
 * The content route names the file in its `content-disposition`; the download
 * uses that name and falls back to the report's id only where the header is
 * absent. Fetched rather than linked, because under `dev-header` the identity
 * travels as request headers a plain link cannot carry, and under a cookie it
 * makes no difference.
 */

const base = (source: Source): string => {
  if (source.base === null) {
    throw new ReadFailedError('a report', 0, 'The fixture holds no reports.');
  }
  return source.base.replace(/\/+$/u, '');
};

/** `GET /:id/reports`: every report generated for the assessment, newest first, in the route's order. */
export async function listReports(source: Source, assessmentId: string): Promise<Report[]> {
  const response = await fetch(`${base(source)}/v1/assessments/${assessmentId}/reports`, {
    headers: identityHeaders(source),
    credentials: 'same-origin',
  });
  if (!response.ok) {
    throw new ReadFailedError('the reports', response.status, await detailOf(response));
  }
  const payload = (await response.json()) as { reports?: unknown };
  if (!Array.isArray(payload.reports)) {
    throw new ReadFailedError('the reports', 200, 'The response carried no reports array.');
  }
  return payload.reports.map((row) => ReportSchema.parse(row));
}

export interface ReportContent {
  /** The stored markdown, exactly as served. */
  text: string;
  /** The filename the route named, or the id where it named none. */
  filename: string;
}

/**
 * The filename inside a `content-disposition` header, or `null`.
 *
 * Reads the one form the route writes — `attachment; filename="<encoded>"` —
 * and decodes it. Anything else answers `null` and the caller falls back;
 * this is not a parser for the header's grammar and does not claim to be.
 */
export function filenameFrom(disposition: string | null): string | null {
  if (disposition === null) return null;
  const match = /filename="([^"]+)"/u.exec(disposition);
  if (match?.[1] === undefined) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

/** `GET /v1/reports/:reportId/content`: the bytes, as text, with the route's filename. */
export async function readReport(source: Source, report: Report): Promise<ReportContent> {
  const response = await fetch(`${base(source)}/v1/reports/${report.report_id}/content`, {
    headers: identityHeaders(source),
    credentials: 'same-origin',
  });
  if (!response.ok) {
    throw new ReadFailedError('the report', response.status, await detailOf(response));
  }
  const text = await response.text();
  if (text.length === 0) {
    // An empty body is a broken read and not an empty report: the store
    // refuses to serve bytes whose digest does not match, so a 200 with
    // nothing in it is a transport fault rather than a document.
    throw new ReadFailedError('the report', 200, 'The response carried no content.');
  }
  return {
    text,
    filename: filenameFrom(response.headers.get('content-disposition')) ?? `${report.report_id}.md`,
  };
}
